// The company letterhead, as HTML, for the documents this app builds itself.
//
// Mirrors cid_admin_server/src/templates/booking-forms/partials/header.hbs — the
// letterhead the server prints on booking forms, library letters and the
// co-share form — and the admin portal's DocumentLetterhead component. Change
// one and change the others, or the same company prints three letterheads.
//
// The mark is the wordmark shipped with the app rather than document_settings'
// company_logo_url, for the same reason the servers inline theirs: the
// letterhead must look identical everywhere and must not wait on storage. The
// licence number belongs to the licence mark, not to the company: it prints
// under the licence type — "Ejen Harta Tanah" then "E(1)1855/1" — and the name
// line carries the registration number alone, "(1285533-A)".

const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

export const LETTERHEAD_LOGO = '/cid_fulllogo.png';

/**
 * @param {Object} s        document_settings map
 * @param {Object} [opts]
 * @param {Object} [opts.contact]  { address, phone, fax, email, website } — defaults
 *                                 to the settings' own company_* contact fields
 * @param {Object} [opts.colors]   { text, muted } — the statement prints its
 *                                 letterhead in its own palette
 * @returns {string} HTML for the letterhead block (no bottom rule; callers own that)
 */
export function letterheadHtml(s = {}, opts = {}) {
  const c = opts.contact || {
    address: s.company_address,
    phone: s.company_phone,
    fax: s.company_fax,
    email: s.company_email,
    website: s.company_website,
  };
  const text = opts.colors?.text || '#000';
  const muted = opts.colors?.muted || text;

  const reg = s.company_registration_no ? esc(`(${s.company_registration_no})`) : '';

  const contactRows = [
    [c.phone ? `T : ${esc(c.phone)}` : '', c.fax ? `F : ${esc(c.fax)}` : ''],
    [c.email ? `E : ${esc(c.email)}` : '', c.website ? `W : ${esc(c.website)}` : ''],
  ].filter((row) => row[0] || row[1]);

  return `
    <div style="display:flex;align-items:center;justify-content:space-between;gap:14px">
      <div style="display:flex;align-items:center;gap:14px;flex:1;min-width:0">
        <img src="${LETTERHEAD_LOGO}" alt="${esc(s.company_name || 'CID Realtors')}" style="height:62px;width:auto;flex:none" />
        <div style="flex:1;min-width:0;border-left:1px solid ${text};padding-left:14px">
          <div style="font-size:18px;font-weight:bold;line-height:1.25;color:${text}">${esc(s.company_name || '')}${reg ? ` <span style="font-size:10.5px;font-weight:normal;color:${muted}">${reg}</span>` : ''}</div>
          ${c.address ? `<div style="font-size:10px;line-height:1.45;margin-top:3px;white-space:pre-line;color:${muted}">${esc(c.address)}</div>` : ''}
          ${contactRows.map(([left, right]) => `
          <div style="display:flex;gap:12px;font-size:10px;line-height:1.45;margin-top:2px;color:${muted}">
            <span style="flex:1;min-width:0">${left}</span><span style="flex:1;min-width:0">${right}</span>
          </div>`).join('')}
          ${s.sst_no ? `<div style="font-size:9px;font-weight:bold;margin-top:2px;color:${muted}">(SST No: ${esc(s.sst_no)})</div>` : ''}
        </div>
      </div>
      <div style="flex:none;text-align:center;min-width:76px">
        ${s.license_logo_url ? `<img src="${esc(s.license_logo_url)}" alt="${esc(s.license_type || 'License')}" style="height:38px" />` : ''}
        ${s.license_type ? `<div style="font-size:8px;font-style:italic;margin-top:2px;line-height:1.3;color:${muted}">${esc(s.license_type)}</div>` : ''}
        ${s.license_no ? `<div style="font-size:8px;margin-top:1px;line-height:1.3;color:${muted}">${esc(s.license_no)}</div>` : ''}
      </div>
    </div>`;
}
